import type {
  TouchEvent as ReactTouchEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useRef, useCallback, useState, useEffect } from 'react';

export const useSwipeDownToClose = (onClose?: () => void) => {
  const swipeRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef(0);
  const onCloseRef = useRef(onClose);
  const masterOffset = useRef(0);
  const startY = useRef(0);
  const threshold = 50;

  const updatePosition = () => {
    if (animationRef.current !== undefined) {
      if (swipeRef && null !== swipeRef.current) {
        swipeRef.current.style.transform = `translate3d(0, ${masterOffset.current}px, 0)`;
      }
    }
  };

  const [listenToInteraction, setListenToInteraction] = useState<
    'idle' | 'mousemove' | 'touchmove'
  >('idle');

  const move = useCallback((offset: number) => {
    if (offset > 0) {
      masterOffset.current = offset;
      animationRef.current = requestAnimationFrame(updatePosition);
    }
  }, []);

  const onTouchMove = useCallback(
    (event: TouchEvent): void => {
      const offset = event.touches[0].clientY - startY.current;
      move(offset);
    },
    [move]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent): void => {
      event.stopPropagation();
      const offset = event.clientY - startY.current;
      move(offset);
    },
    [move]
  );

  const onTouchStart = useCallback(
    (event: ReactTouchEvent): void => {
      if (swipeRef?.current) swipeRef.current.style.transition = 'none';
      startY.current = event.touches[0].clientY;
      setListenToInteraction('touchmove');
    },
    [swipeRef]
  );

  const onMouseDown = useCallback(
    (event: ReactMouseEvent): void => {
      if (swipeRef?.current) swipeRef.current.style.transition = 'none';
      startY.current = event.clientY;
      setListenToInteraction('mousemove');
    },
    [swipeRef]
  );

  const onInteractionEnd = useCallback((): void => {
    cancelAnimationFrame(animationRef.current);
    setListenToInteraction('idle');
    if (swipeRef && null !== swipeRef.current) {
      swipeRef.current.style.removeProperty('transform');
      swipeRef.current.style.removeProperty('transition');
    }
    if (Math.abs(masterOffset.current) > threshold) {
      onCloseRef.current?.();
    }
    masterOffset.current = 0;
  }, [onCloseRef, swipeRef, threshold]);

  useEffect(() => {
    if (listenToInteraction !== 'mousemove') {
      return;
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onInteractionEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onInteractionEnd);
    };
  }, [listenToInteraction, onMouseMove, onInteractionEnd]);

  useEffect(() => {
    if (listenToInteraction !== 'touchmove') {
      return;
    }
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onInteractionEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onInteractionEnd);
    };
  }, [listenToInteraction, onTouchMove, onInteractionEnd]);

  return {
    swipeRef,
    onTouchStart,
    onMouseDown,
  };
};
